import { Copy, KeyRound, MessageCircle, Smartphone, Trash2 } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Button, Field, Input, Modal } from "./ui";

export function StudentAccessModal({ student, onClose }: { student: any | null; onClose: () => void }) {
  const [info, setInfo] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<{ email: string; password: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!student) return;
    setResult(null); setPassword("");
    setEmail(student.portal_email || student.email || "");
    api.get(`/admin/students/${student.id}/access`).then((r) => setInfo(r.data)).catch(() => setInfo({ exists: false }));
  }, [student]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/students/${student.id}/access`, { email, password: password || null });
      setResult({ email: data.email, password: data.password });
      setInfo({ ...(info || {}), exists: true, email: data.email, must_change_password: true });
      toast.success("Akses portal siswa dibuat");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const revoke = async () => {
    try { await api.delete(`/admin/students/${student.id}/access`); toast.success("Akses dicabut"); setInfo({ exists: false }); setResult(null); } catch (err) { toast.error(errMsg(err)); }
  };
  const credText = () => `*Portal Siswa Yutaka Edu-Finance*\n\nHalo ${student?.name}, berikut akses aplikasi cicilan Anda:\n📱 Link install: ${window.location.origin}/siswa-app\n✉️ Email: ${result!.email}\n🔑 Password awal: ${result!.password}\n\nLangkah:\n1. Buka link di HP, login, lalu ganti password.\n2. Android/Huawei: menu ⋮ → "Tambahkan ke layar utama". iPhone: Safari → Bagikan → "Tambah ke Layar Utama", buka dari ikon.\n3. Tekan "Aktifkan notifikasi HP" (wajib) untuk pengingat cicilan 09:00 & 19:00 JST.\n\nAplikasi wajib terpasang & notifikasi aktif hingga pinjaman lunas.`;
  const copy = () => { navigator.clipboard?.writeText(credText()); toast.success("Teks tersalin — kirim ke siswa via WA/Email"); };
  const waNum = (raw: string) => { let n = (raw || "").replace(/\D/g, ""); if (n.startsWith("0")) n = "62" + n.slice(1); return n; };
  const sendWa = (num: string, label: string) => {
    window.open(`https://wa.me/${waNum(num)}?text=${encodeURIComponent(credText())}`, "_blank");
    toast.success(`WhatsApp ke ${label} dibuka — tekan Kirim di WA`);
  };

  return (
    <Modal open={!!student} onClose={onClose} title={student ? `Akses Portal HP · ${student.name}` : ""} testId="student-access-modal">
      {student && (
        <div className="space-y-4">
          {info?.exists && !result && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1" data-testid="student-access-info">
              <p className="flex items-center gap-2 font-semibold"><Smartphone className="h-4 w-4 text-primary" /> Akses aktif · {info.email}</p>
              <p className="text-xs text-muted-foreground">Login terakhir: {info.last_login ? fmtDateTime(info.last_login) : "belum pernah"} · {info.push_devices || 0} HP menerima notifikasi{info.must_change_password ? " · belum ganti password awal" : ""}{info.terms_accepted_at ? ` · ketentuan disetujui ${fmtDateTime(info.terms_accepted_at)}` : " · belum menyetujui ketentuan"}</p>
            </div>
          )}
          {result ? (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-sm space-y-1" data-testid="student-access-result">
              <p className="font-semibold text-emerald-800 dark:text-emerald-200">Kredensial siswa (tampil sekali):</p>
              <p className="num">Alamat: <b>{window.location.origin}/siswa-app</b></p>
              <p>Email: <b data-testid="student-access-result-email">{result.email}</b></p>
              <p>Password awal: <b className="num" data-testid="student-access-result-password">{result.password}</b></p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={copy} data-testid="student-access-copy"><Copy className="h-3.5 w-3.5" /> Salin</Button>
                {(student.phone || info?.phone) && <Button type="button" size="sm" onClick={() => sendWa(student.phone || info.phone, "siswa")} data-testid="student-access-wa-student"><MessageCircle className="h-3.5 w-3.5" /> WA Siswa</Button>}
                {(student.guardian_phone || info?.guardian_phone) && <Button type="button" size="sm" variant="outline" onClick={() => sendWa(student.guardian_phone || info.guardian_phone, "wali")} data-testid="student-access-wa-guardian"><MessageCircle className="h-3.5 w-3.5" /> WA Wali</Button>}
              </div>
              {!(student.phone || info?.phone) && !(student.guardian_phone || info?.guardian_phone) && <p className="text-xs text-amber-700 mt-1">Nomor WA siswa/wali belum diisi di data siswa — gunakan Salin.</p>}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Field label="Email siswa (untuk login)"><Input type="email" required data-testid="student-access-email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
              <Field label="Password awal" hint="Kosongkan untuk dibuat otomatis · siswa wajib menggantinya saat pertama masuk"><Input data-testid="student-access-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="otomatis" /></Field>
              <div className="flex justify-between gap-2 pt-1">
                {info?.exists ? <Button type="button" variant="danger" size="sm" onClick={revoke} data-testid="student-access-revoke"><Trash2 className="h-3.5 w-3.5" /> Cabut akses</Button> : <span />}
                <Button type="submit" disabled={busy} data-testid="student-access-submit"><KeyRound className="h-4 w-4" /> {info?.exists ? "Reset password / ubah email" : "Buat akses"}</Button>
              </div>
            </form>
          )}
          <p className="text-xs text-muted-foreground">Siswa hanya melihat detail cicilan, kwitansi/tagihan miliknya, status pengembalian dokumen, dan menerima pengingat 09:00 & 19:00 JST.</p>
        </div>
      )}
    </Modal>
  );
}
